import React, { useRef, useEffect, useCallback, useState } from "react";
import "./ChatConsole.css";
import { useMessageStore } from "../store/store";

// Link style object (constant, reused for all links)
const LINK_STYLE = {
    cursor: 'pointer',
    border: '1px solid gray',
    padding: '2px 4px',
    borderRadius: '4px',
    backgroundColor: '#444444'
};

// Style cache for segment rendering - avoids creating new objects on each render
const styleCache = new Map();
const getSegmentStyle = (color, fontWeight, textDecoration, isLink) => {
    const key = `${color}-${fontWeight}-${textDecoration}-${isLink}`;
    if (!styleCache.has(key)) {
        const baseStyle = { color, fontWeight, textDecoration };
        styleCache.set(key, isLink ? { ...baseStyle, ...LINK_STYLE } : baseStyle);
    }
    return styleCache.get(key);
};

function ChatConsole({ sendMessage, consoleId = 'primary' }) {
    const ref = useRef(null);
    const [showCopyToast, setShowCopyToast] = useState(false);
    
    // Handle text selection and auto-copy
    const handleMouseUp = useCallback(async () => {
        const selection = window.getSelection();
        const selectedText = selection?.toString().trim();
        
        if (selectedText && selectedText.length > 0 && navigator.clipboard) {
            try {
                await navigator.clipboard.writeText(selectedText);
                setShowCopyToast(true);
                setTimeout(() => setShowCopyToast(false), 2000);
            } catch (err) {
                // Clipboard access denied - silently ignore
                // This can happen if the browser blocks clipboard access
                console.debug('Clipboard write failed:', err.message);
            }
        }
    }, []);
    
    // Separate subscriptions to avoid infinite re-render loops
    // Use different message arrays based on consoleId
    const messages = useMessageStore((state) => 
        consoleId === 'secondary' ? state.messages2 : state.messages
    );
    const messageVersion = useMessageStore((state) => 
        consoleId === 'secondary' ? state.messageVersion2 : state.messageVersion
    );

    // Scroll to bottom when messages change - use messageVersion since length stays at MAX after limit
    // Double RAF ensures DOM is fully painted before scrolling (fixes long text cut-off)
    useEffect(() => {
        if (ref.current) {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (ref.current) {
                        ref.current.scrollTop = ref.current.scrollHeight;
                    }
                });
            });
        }
    }, [messageVersion]);

    // Render timestamp with CSS pseudo-elements for brackets (single element)
    const renderTimestamp = useCallback((timestampDisplay) => {
        if (!timestampDisplay) return null;
        return <span className="message-timestamp">{timestampDisplay}</span>;
    }, []);

    // Render formatted text segment from data object with cached styles
    const renderSegment = useCallback((segment, key) => {
        const style = getSegmentStyle(
            segment.color,
            segment.fontWeight,
            segment.textDecoration,
            segment.isLink
        );
        
        const handleClick = segment.isLink && sendMessage 
            ? () => sendMessage(segment.linkCommand)
            : undefined;
        
        return (
            <span 
                key={key} 
                className={segment.className}
                style={style} 
                onClick={handleClick}
            >
                {segment.text}
            </span>
        );
    }, [sendMessage]);

    // Messages are pre-processed in store - just render directly
    return (
        <div ref={ref} className='scroll-chat' onMouseUp={handleMouseUp}>
            <ul>
                {messages.map((line) => (
                    <p className="messages" key={line.key}>
                        {renderTimestamp(line.timestampDisplay)}
                        {line.content.map((segment, idx) => 
                            renderSegment(segment, segment.key || idx)
                        )}
                    </p>
                ))}
            </ul>
            {showCopyToast && (
                <div className="copy-toast">Selected text copied!</div>
            )}
        </div>
    );
}

export default ChatConsole;
